import React from 'react';
import styled from '../../../../style/styled';
import { useSelector } from 'react-redux';
import { ScrollPosition } from 'react-lazy-load-image-component';
import { SCREEN_WIDTH_BREAK_SMALL } from '../../../../style/SIZES';
import {
  selectLanguage,
  selectPlaylistsMapInCart,
} from '../../../../store/app/selectors';
import {
  ContentSectionWidgetImageAspectRatioBox,
  ContentSectionWidgetImageAspectRatioBoxContent,
  ContentSectionWidgetImageAspectRatioBoxContentWrapper,
} from '../../styledComponents';
import { ContentSectionWidgetNewContainer } from './ContentSectionWidgetNewContainer';
import { TourWidgetNewOfflineStatusIndicator } from './TourWidgetNewOfflineStatusIndicator';
import { TourWidgetNewDefaultCheckInButton } from './TourWidgetNewDefaultCheckInButton';
import { TourWidgetNewGoToGateButton } from './TourWidgetNewGoToGateButton';
import { TourWidgetNewBookNowButton } from './TourWidgetNewBookNowButton';
import { getExpiryInfo } from '../getExpiryInfo';
import { TourWidgetNewPrice } from './TourWidgetNewPrice';
import { TourWidgetNewTestTourIndicator } from './TourWidgetNewTestTourIndicator';
import { COLORS } from '../../../../style/colors';
import { TourWidgetNewContinueTourButton } from './TourWidgetNewContinueTourButton';
import { useTranslation } from 'react-i18next';
import { getI18nFieldValue } from '../../../../features/i18n/getI18nFieldValue';
import { Asset, Country, I18nInfoField, Price } from '../../../../types';
import { CurrencyCode } from '../../../../graphql/globalTypes';
import {
  GiDigitalTrace,
  ImHeadphones,
  ImImage,
  RiWalkFill,
} from 'react-icons/all';
import { getItineraryPageRouteForItineraryPage } from '../../../../utils/getItineraryPageRouteForItineraryPage';
import { useHistory } from 'react-router';
import { getImagesCount } from '../tourInfo/TourInfoLotsToDoSection';
import { TourWidgetNewPastThumbnail } from './TourWidgetNewPastThumbnail';

export type TourWidgetNewTour = {
  id: string;
  internalReference: string;
  trial: boolean;
  published: boolean;
  purchased: boolean;
  nameI18n: I18nInfoField;
  validityPeriodMonths: number | null;
  price: Price;
  pastThumbnail: Asset | null;
  presentThumbnail: Asset | null;
  descriptionI18n: I18nInfoField | null;
  countries: Country[] | [];
  viewpoints: Array<{ imagesCount: number }> | [];
  trailLength: number | null;
  timePeriod: string | null;
  audioDurationMinutes: number | null;
  currencyCode: CurrencyCode;
  unitPrice: number;
  priceInCents: boolean;
  pinned?: boolean;
};

export interface Props {
  buyTour: (tourID: string) => void;
  expiresAt?: string | null;
  isGift?: boolean;
  tour: TourWidgetNewTour;
  LeftButton?: React.ReactChild;
  RightButton?: React.ReactChild;
  scrollPosition?: ScrollPosition;
  opensItineraryPage?: boolean;
  withPriceHidden?: boolean;
  internalReference?: string;
}

export const TourWidgetNew: React.FC<Props> = ({
  buyTour,
  expiresAt,
  tour,
  LeftButton,
  RightButton,
  isGift,
  scrollPosition,
  opensItineraryPage = false,
  withPriceHidden = false,
  internalReference,
}) => {
  // const dispatch = useDispatch();
  const playlistsMapInCart = useSelector(selectPlaylistsMapInCart);
  const language = useSelector(selectLanguage);
  const { expiresIn, expiresSoon } = getExpiryInfo(expiresAt, language);
  const { t } = useTranslation();

  const isInCart = Boolean(playlistsMapInCart[tour.id]);

  // const nonInteractiveImageURI = tour.presentThumbnail?.uri;
  const interactiveImageURI = tour.pastThumbnail?.uri;

  let leftButton: React.ReactNode = null;

  if (LeftButton) {
    leftButton = LeftButton;
  } else if (tour.purchased && tour.pinned) {
    leftButton = <TourWidgetNewContinueTourButton tourID={tour.id} />;
  } else if (tour.purchased && !tour.pinned) {
    leftButton = <TourWidgetNewGoToGateButton tourID={tour.id} />;
  } else if (!tour.purchased && !isInCart) {
    leftButton = (
      <TourWidgetNewBookNowButton
        tourID={tour.id}
        buy={() => buyTour(tour.id)}
      />
    );
  } else {
    leftButton = <TourWidgetNewDefaultCheckInButton tourID={tour.id} />;
  }

  // const rightButton = RightButton || (
  //   <TourWidgetNewItineraryButtonConditional
  //     tourID={tour.id}
  //     internalReference={tour.internalReference}
  //     opensItineraryPage={opensItineraryPage}
  //   />
  // );

  const priceDisplayed =
    !withPriceHidden && !tour.trial && (!tour.purchased || isGift) && !isInCart;
  const downloadable = (tour.purchased || tour.trial) && !isGift;
  const tourName = getI18nFieldValue(tour.nameI18n, language);
  const history = useHistory();
  const gotoTourDetails = () => {
    history.push(
      getItineraryPageRouteForItineraryPage({
        page: window.location.pathname,
        internalReference: tour.internalReference as string,
      })
    );
  };
  return (
    <ContentSectionWidgetNewContainer interactive={!tour.purchased}>
      <ContentSectionWidgetImageAspectRatioBox onClick={gotoTourDetails}>
        <ContentSectionWidgetImageAspectRatioBoxContentWrapper>
          <ContentSectionWidgetImageAspectRatioBoxContent>
            {/* Test tour indicator */}
            {!tour.published && <TourWidgetNewTestTourIndicator />}

            {/* Only visible on desktop */}
            {/*{!isMobile && nonInteractiveImageURI && (*/}
            {/*  <TourWidgetNewPresentThumbnail*/}
            {/*    scrollPosition={scrollPosition}*/}
            {/*    imageURI={nonInteractiveImageURI}*/}
            {/*  />*/}
            {/*)}*/}

            {/* This image moves to left when hovered */}
            {interactiveImageURI && (
              <TourWidgetNewPastThumbnail
                scrollPosition={scrollPosition}
                imageURI={interactiveImageURI}
              />
            )}

            {/* Download button */}
            {downloadable && (
              <TourWidgetNewOfflineStatusIndicator
                playlistID={tour.id}
                playlistName={tourName}
              />
            )}

            {/* {tour.pinned && <TourWidgetPinnedIndicator />} */}
          </ContentSectionWidgetImageAspectRatioBoxContent>
        </ContentSectionWidgetImageAspectRatioBoxContentWrapper>
      </ContentSectionWidgetImageAspectRatioBox>
      <CardContent onClick={gotoTourDetails}>
        <h4> {tour.countries && tour.countries[0]?.name.toUpperCase()}</h4>
        <h2>{tourName?.toUpperCase()}</h2>
        <FadeOutText>
          {getI18nFieldValue(tour.descriptionI18n, language)}
        </FadeOutText>
        <CardIconWrapper>
          <CardIconContentDoubleColumn>
            <GiDigitalTrace style={{ marginRight: 5 }} />
            {tour?.viewpoints?.length} digital reconstructions
          </CardIconContentDoubleColumn>
          <CardIconContentSingleColumn>
            <RiWalkFill style={{ marginRight: 5 }} />
            {tour?.trailLength} m
          </CardIconContentSingleColumn>
        </CardIconWrapper>
        <CardIconWrapper>
          <CardIconContentDoubleColumn>
            <ImHeadphones style={{ marginRight: 5 }} />
            {tour?.audioDurationMinutes} minutes
          </CardIconContentDoubleColumn>
          <CardIconContentSingleColumn>
            <ImImage style={{ marginRight: 5 }} />
            {tour.viewpoints && getImagesCount(tour.viewpoints)} images
          </CardIconContentSingleColumn>
        </CardIconWrapper>
      </CardContent>
      <CardAction>
        <div>{leftButton}</div>
        {/* Price indicator */}
        <div>
          {priceDisplayed ? (
            <TourWidgetNewPrice
              currencyCode={tour.currencyCode}
              price={tour.unitPrice}
              priceInCents={tour.priceInCents}
            />
          ) : tourName.includes('Free') ? (
            <FreeTourPriceComponent>FREE</FreeTourPriceComponent>
          ) : null}
        </div>
      </CardAction>

      {expiresIn && (
        <TourWidgetNewExpiresAtContainer expiresSoon={expiresSoon}>
          <span
          // onClick={(e) => {
          //   e.stopPropagation();
          // }}
          >
            {t('Expires in')} {expiresIn}
          </span>

          {expiresSoon && (
            <ExpiryMessageContainer>
              {t(
                'Once a purchased tour expires, you will need to purchase it again.'
              )}
            </ExpiryMessageContainer>
          )}
        </TourWidgetNewExpiresAtContainer>
      )}
    </ContentSectionWidgetNewContainer>
  );
};

const TourWidgetNewExpiresAtContainer = styled.div<{ expiresSoon?: boolean }>`
  text-align: left;
  margin-top: 4px;
  color: ${({ expiresSoon }) => (expiresSoon ? COLORS.errorColor : 'inherit')};
  font-weight: ${({ expiresSoon }) => (expiresSoon ? 'bold' : 'auto')};
  position: relative;

  @media only screen and (max-width: ${SCREEN_WIDTH_BREAK_SMALL}px) {
    margin-top: 0;
  }
`;

const ExpiryMessageContainer = styled.div`
  margin-top: 4px;
  position: absolute;
  background: #eee;
  max-width: 300px;
  padding: 8px;
  border-radius: 4px;
  z-index: 4;
  display: none;
  border: 1px solid #ccc;
  color: ${(props) => props.theme.palette.common.white};
  font-weight: normal;
  font-size: 12px;

  ${TourWidgetNewExpiresAtContainer}:hover & {
    display: inherit;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  padding: 15px;
  border-top: none;
  max-height: 262px;

  h4 {
    color: #777777;
    font-family: 'Roboto Black';
    font-size: 16px;
  }

  h2 {
    margin: 5px 0;
    font-size: 18px;
    font-family: 'Roboto Black';
    min-height: 30px;
  }

  p {
    margin: 10px 0;
  }
`;
const FadeOutText = styled.p`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: justify;
  min-height: 58px;
`;
const CardIconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  margin: 5px 0;
`;

const CardIconContentDoubleColumn = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1.5;
  align-items: center;
  margin-right: 10px;
`;
const CardIconContentSingleColumn = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  align-items: center;
`;

const CardAction = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #ccc;
  padding: 10px 20px;
  cursor: default;
`;

const FreeTourPriceComponent = styled.div`
  color: #0066ff;
  font-weight: bold;
  text-decoration: none;
  text-decoration-thickness: 0;
  margin-right: 0;
  margin-left: 0;
  font-size: 23px;
`;
